import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  fetchImportJob,
  fetchImportJobChanges,
} from "@accredo/fe-shared/services/importJobService.js";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

function statusChipColor(status) {
  if (["completed", "rolled_back"].includes(status)) return "success";
  if (["processing", "rollback_processing"].includes(status)) return "warning";
  if (["failed", "cancelled", "rollback_failed"].includes(status))
    return "error";
  return "default";
}

function actionChipColor(action) {
  if (action === "created") return "success";
  if (action === "updated") return "info";
  if (action === "skipped") return "default";
  if (action === "failed") return "error";
  if (action === "deleted") return "warning";
  return "default";
}

const ACTION_FILTERS = {
  all: { label: "Alle", actions: [] },
  changed: {
    label: "Erstellt / Aktualisiert",
    actions: ["created", "updated"],
  },
  skipped: { label: "Übersprungen", actions: ["skipped"] },
  failed: { label: "Fehlgeschlagen", actions: ["failed"] },
  deleted: { label: "Gelöscht", actions: ["deleted"] },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function FieldChangesList({ field_changes, action }) {
  if (!field_changes || Object.keys(field_changes).length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2, listStyle: "none" }}>
      {Object.entries(field_changes).map(([field, change]) => (
        <Box
          component="li"
          key={field}
          sx={{ fontFamily: "monospace", fontSize: "0.7rem", lineHeight: 1.6 }}
        >
          <strong>{field}:</strong>{" "}
          {action === "created" ? (
            String(change.new ?? "—")
          ) : (
            <>
              <span style={{ color: "#d32f2f" }}>
                {String(change.old ?? "—")}
              </span>
              {" → "}
              <span style={{ color: "#2e7d32" }}>
                {String(change.new ?? "—")}
              </span>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
}

function SourceRowList({ source_row }) {
  if (!source_row || Object.keys(source_row).length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2, listStyle: "none" }}>
      {Object.entries(source_row).map(([column, value]) => (
        <Box
          component="li"
          key={column}
          sx={{ fontFamily: "monospace", fontSize: "0.7rem", lineHeight: 1.6 }}
        >
          <strong>{column}:</strong>{" "}
          {value === "" ? "(leer)" : String(value ?? "—")}
        </Box>
      ))}
    </Box>
  );
}

function SourceMetaTooltip({ source_meta }) {
  if (!source_meta) return null;
  const lines = Object.entries(source_meta)
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
  return (
    <Tooltip
      title={
        <Box
          sx={{
            whiteSpace: "pre",
            fontFamily: "monospace",
            fontSize: "0.7rem",
          }}
        >
          {lines}
        </Box>
      }
    >
      <InfoIcon
        sx={{
          fontSize: 14,
          color: "text.secondary",
          ml: 0.5,
          verticalAlign: "middle",
          cursor: "help",
        }}
      />
    </Tooltip>
  );
}

export default function ImportJobDetailModal({ open, onClose, jobId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [job, setJob] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected_task, setSelectedTask] = useState(null);
  const [changes, setChanges] = useState([]);
  const [changes_total, setChangesTotal] = useState(0);
  const [action_counts, setActionCounts] = useState({});
  const [page_index, setPageIndex] = useState(0);
  const [page_size, setPageSize] = useState(25);
  const [changes_loading, setChangesLoading] = useState(false);
  const [action_filter, setActionFilter] = useState("all");

  useEffect(() => {
    if (!open || !jobId) return;
    setLoading(true);
    setSelectedTask(null);
    setChanges([]);
    setActionFilter("all");
    fetchImportJob(jobId)
      .then((res) => {
        setJob(res.data);
        setTasks(res.tasks ?? []);
      })
      .catch(() =>
        enqueueSnackbar("Job konnte nicht geladen werden", { variant: "error" })
      )
      .finally(() => setLoading(false));
  }, [open, jobId, enqueueSnackbar]);

  useEffect(() => {
    if (!selected_task) return;
    loadChanges(selected_task, 0, page_size);
  }, [selected_task, action_filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const is_running = job
    ? ["processing", "rollback_processing"].includes(job.status)
    : false;

  useEffect(() => {
    if (!open || !jobId || !is_running) return;
    const id = setInterval(() => {
      fetchImportJob(jobId)
        .then((res) => {
          setJob(res.data);
          setTasks(res.tasks ?? []);
        })
        .catch(() => {});
      if (selected_task) {
        fetchImportJobChanges(jobId, {
          record_type: selected_task.record_type,
          actions: ACTION_FILTERS[action_filter].actions,
          page: page_index + 1,
          per_page: page_size,
        })
          .then(applyChangesResponse)
          .catch(() => {});
      }
    }, 5000);
    return () => clearInterval(id);
  }, [
    open,
    jobId,
    is_running,
    selected_task,
    page_index,
    page_size,
    action_filter,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyChangesResponse(res) {
    setChanges(res.data ?? []);
    setChangesTotal(res.total ?? 0);
    setActionCounts(res.action_counts ?? {});
  }

  function loadChanges(task, next_page_index, next_page_size) {
    setChangesLoading(true);
    fetchImportJobChanges(jobId, {
      record_type: task.record_type,
      actions: ACTION_FILTERS[action_filter].actions,
      page: next_page_index + 1,
      per_page: next_page_size,
    })
      .then((res) => {
        applyChangesResponse(res);
        setPageIndex(next_page_index);
        setPageSize(next_page_size);
      })
      .catch(() =>
        enqueueSnackbar("Änderungen konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setChangesLoading(false));
  }

  function handleTaskClick(task) {
    if (selected_task?.id === task.id) {
      setSelectedTask(null);
      setChanges([]);
    } else {
      setSelectedTask(task);
    }
  }

  const task_columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "#",
        size: 70,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        id: "record_type",
        header: "Entitätstyp",
        accessorFn: (row) => row.record_type_label ?? row.record_type ?? "—",
      },
      {
        accessorKey: "original_filename",
        header: "Datei",
        Cell: ({ row }) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
          >
            {row.original.original_filename ?? "—"}
            <SourceMetaTooltip source_meta={row.original.source_meta} />
          </Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.status_label}
            color={statusChipColor(row.original.status)}
          />
        ),
      },
      {
        accessorKey: "total_rows",
        header: "Gesamt",
        size: 90,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "processed_rows",
        header: "Verarbeitet",
        size: 110,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "skipped_rows",
        header: "Übersprungen",
        size: 120,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        id: "errors",
        header: "Fehler",
        size: 90,
        accessorFn: (row) => row.error_summary?.length,
        Cell: ({ cell }) => {
          const count = cell.getValue();
          if (count === undefined) return "—";
          return (
            <Typography
              variant="body2"
              color={count > 0 ? "error.main" : "text.primary"}
            >
              {count}
            </Typography>
          );
        },
      },
      {
        accessorKey: "created_at",
        header: "Erstellt am",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
    ],
    []
  );

  const change_columns = useMemo(
    () => [
      {
        accessorKey: "row_number",
        header: "Zeile",
        size: 80,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {cell.getValue() ?? "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "external_ref",
        header: "Ext. Ref",
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
          >
            {cell.getValue() ?? "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "entity_label",
        header: "Datensatz",
        Cell: ({ row }) => {
          const { entity_id, entity_label } = row.original;
          if (entity_label) {
            return <Typography variant="body2">{entity_label}</Typography>;
          }
          return (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: entity_id ? "italic" : "normal",
              }}
            >
              {entity_id ? "nicht mehr vorhanden" : "—"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "action",
        header: "Aktion",
        size: 140,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.action_label}
            color={actionChipColor(row.original.action)}
          />
        ),
      },
      {
        accessorKey: "skip_reason",
        header: "Grund",
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.75rem" }}
          >
            {cell.getValue() ?? "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "field_changes",
        header: "Felder",
        size: 300,
        Cell: ({ row }) =>
          row.original.action === "failed" ? (
            <SourceRowList source_row={row.original.source_row} />
          ) : (
            <FieldChangesList
              field_changes={row.original.field_changes}
              action={row.original.action}
            />
          ),
      },
      {
        accessorKey: "error_message",
        header: "Fehler",
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ color: "error.main", fontSize: "0.75rem" }}
          >
            {cell.getValue() ?? "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  const filter_tabs = Object.entries(ACTION_FILTERS).map(
    ([key, { label, actions }]) => ({
      key,
      label,
      count: (actions.length ? actions : Object.keys(action_counts)).reduce(
        (sum, action) => sum + (action_counts[action] ?? 0),
        0
      ),
      onClick: setActionFilter,
    })
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        Job #{jobId}
        {job && (
          <Chip
            size="small"
            label={job.status_label}
            color={statusChipColor(job.status)}
            sx={{ ml: 2, verticalAlign: "middle" }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Job meta */}
            {job && (
              <Box display="flex" gap={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Connector
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {job.connector?.name ?? "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Erstellt von
                  </Typography>
                  <Typography variant="body2">
                    {job.created_by?.name ?? "System"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Erstellt am
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(job.created_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Gestartet
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(job.started_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Abgeschlossen
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(job.finished_at)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Tasks */}
            <Box>
              <Typography variant="subtitle2" mb={1}>
                Tasks ({tasks.length})
                {selected_task && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    ml={1}
                  >
                    - Zeile anklicken zum Schließen, andere Zeile zum Wechseln
                  </Typography>
                )}
              </Typography>
              <DataTable
                columns={task_columns}
                data={tasks}
                enableTopToolbar={false}
                enablePagination={false}
                enableBottomToolbar={false}
                enableGlobalFilter={false}
                enableColumnFilters={false}
                enableColumnHiding={false}
                enableSorting={false}
                onRowClick={handleTaskClick}
                getRowId={(row) => String(row.id)}
                rowSelection={selected_task ? { [selected_task.id]: true } : {}}
                testId="import-job-tasks-table"
              />
            </Box>

            {/* Entity changes panel */}
            <Collapse in={!!selected_task}>
              {selected_task && (
                <Box>
                  <Typography variant="subtitle2" mb={1}>
                    Zeilenänderungen —{" "}
                    {selected_task.record_type_label ??
                      selected_task.record_type}
                  </Typography>
                  <DataTable
                    columns={change_columns}
                    data={changes}
                    isLoading={changes_loading}
                    filterTabs={filter_tabs}
                    activeFilterTab={action_filter}
                    pageIndex={page_index}
                    pageSize={page_size}
                    totalRows={changes_total}
                    onChangePage={(next) =>
                      loadChanges(
                        selected_task,
                        next.pageSize === page_size ? next.pageIndex : 0,
                        next.pageSize
                      )
                    }
                    enableGlobalFilter={false}
                    enableColumnFilters={false}
                    enableColumnHiding={false}
                    enableSorting={false}
                    testId="import-job-changes-table"
                  />
                </Box>
              )}
            </Collapse>
          </Box>
        )}
      </DialogContent>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end" }}>
        <DsButton variant="tertiary" onClick={onClose}>
          Schließen
        </DsButton>
      </Box>
    </Dialog>
  );
}

ImportJobDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  jobId: PropTypes.number,
};

FieldChangesList.propTypes = {
  field_changes: PropTypes.object,
  action: PropTypes.string,
};

SourceRowList.propTypes = {
  source_row: PropTypes.object,
};

SourceMetaTooltip.propTypes = {
  source_meta: PropTypes.object,
};
